import { Link } from "react-router-dom";
import { Phone, ArrowUpRight, Check, AlertTriangle, DoorClosed, KeyRound, ShieldCheck, Clock } from "lucide-react";
import { Overline, SectionHeader } from "@/components/Section";
import CTABand from "@/components/CTABand";

const IMG = "https://images.pexels.com/photos/279810/pexels-photo-279810.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const Ouverture = () => {
    return (
        <>
            {/* HERO */}
            <section className="relative border-b border-black/10 grid-bg" data-testid="ouverture-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-16 lg:pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-7 fade-up fade-up-1">
                            <Overline className="mb-5">◉ Service · Urgence</Overline>
                            <h1 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                                Ouverture<br />de porte<br /><span className="text-[#E63946]">sans dégât.</span>
                            </h1>
                            <p className="mt-7 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl">
                                Porte claquée, clés à l'intérieur, serrure bloquée ? Notre artisan ouvre votre porte
                                en quelques minutes, sans abîmer votre serrure dans 9 cas sur 10. Intervention 24h/24, 7j/7 dans la commune.
                            </p>
                            <div className="mt-9 flex flex-col sm:flex-row gap-3">
                                <a href="tel:+33649501843" data-testid="ouverture-call-btn" className="inline-flex items-center justify-between gap-4 bg-[#E63946] hover:bg-[#D62828] text-white px-6 py-4 transition-colors">
                                    <span className="flex items-center gap-3"><Phone className="h-5 w-5" strokeWidth={2.4} /><span className="font-display font-bold text-lg tracking-tight">06 49 50 18 43</span></span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </a>
                                <Link to="/contact" className="inline-flex items-center justify-between gap-4 bg-[#0A0A0A] text-white px-6 py-4 hover:bg-zinc-800 transition-colors">
                                    <span className="font-semibold text-sm uppercase tracking-[0.15em]">Devis gratuit</span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5 fade-up fade-up-2">
                            <img src={IMG} alt="Poignée de porte métal" className="w-full h-[380px] sm:h-[480px] object-cover border border-black/10" loading="eager" />
                        </div>
                    </div>
                </div>
            </section>

            {/* CLAQUEE VS BLOQUEE */}
            <section className="py-20 lg:py-28 bg-white border-b border-black/10" data-testid="ouverture-types">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeader
                        kicker="◉ Diagnostic"
                        title="Porte claquée ou porte bloquée ?"
                        lead="Le diagnostic change tout : la technique d'ouverture, le temps d'intervention et le tarif. Notre artisan identifie le cas dès l'appel."
                    />
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="bg-white border border-black/10 p-7 sm:p-9">
                            <div className="h-12 w-12 grid place-items-center bg-[#0A0A0A] text-white">
                                <DoorClosed className="h-5 w-5" strokeWidth={2.2} />
                            </div>
                            <h3 className="mt-7 font-display font-bold text-xl sm:text-2xl uppercase tracking-tight">Porte claquée</h3>
                            <p className="mt-3 text-zinc-600 leading-relaxed">
                                La serrure n'est pas verrouillée à clé. Seul le pêne demi-tour retient la porte.
                                L'ouverture se fait par carte/radio sans aucun dégât, en quelques minutes.
                            </p>
                            <ul className="mt-6 space-y-2.5 text-sm">
                                {["Aucune dégradation de la serrure", "Intervention rapide (5 à 15 min)", "Tarif accessible"].map((t) => (
                                    <li key={t} className="flex items-start gap-2.5"><Check className="h-4 w-4 text-[#E63946] mt-0.5 flex-shrink-0" strokeWidth={2.6} /><span>{t}</span></li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-white border border-black/10 p-7 sm:p-9">
                            <div className="h-12 w-12 grid place-items-center bg-[#E63946] text-white">
                                <AlertTriangle className="h-5 w-5" strokeWidth={2.2} />
                            </div>
                            <h3 className="mt-7 font-display font-bold text-xl sm:text-2xl uppercase tracking-tight">Porte bloquée / fermée à clé</h3>
                            <p className="mt-3 text-zinc-600 leading-relaxed">
                                Serrure verrouillée, clé cassée à l'intérieur, mécanisme grippé. L'artisan utilise des techniques d'ouverture
                                fines (crochetage, impressioning) pour préserver la serrure quand c'est possible.
                            </p>
                            <ul className="mt-6 space-y-2.5 text-sm">
                                {["Ouverture fine en priorité, perçage en dernier recours", "Remplacement immédiat possible si nécessaire", "Tarif annoncé avant intervention"].map((t) => (
                                    <li key={t} className="flex items-start gap-2.5"><Check className="h-4 w-4 text-[#E63946] mt-0.5 flex-shrink-0" strokeWidth={2.6} /><span>{t}</span></li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section className="py-20 lg:py-28" data-testid="ouverture-process">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <SectionHeader
                                kicker="◉ En pratique"
                                title="Comment se passe l'ouverture ?"
                                lead="Quatre étapes simples, du coup de fil à la remise des clés."
                            />
                        </div>
                        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-black/10 border border-black/10">
                            {[
                                { n: "01", t: "Appel & diagnostic", d: "Vous décrivez la situation. L'artisan vous oriente et annonce un tarif estimatif." },
                                { n: "02", t: "Déplacement rapide", d: "Véhicule équipé, déplacement sous 30 min en moyenne dans votre commune." },
                                { n: "03", t: "Ouverture sans dégât", d: "Techniques fines en priorité, perçage uniquement en dernier recours." },
                                { n: "04", t: "Facture & garantie", d: "Facture détaillée et conseils pour sécuriser votre serrure." },
                            ].map((s) => (
                                <div key={s.n} className="bg-white p-7 sm:p-9 hover:bg-zinc-50 transition-colors">
                                    <div className="font-display text-5xl font-bold text-[#E63946] tracking-tight">{s.n}</div>
                                    <h4 className="mt-5 font-display font-bold text-lg uppercase tracking-tight">{s.t}</h4>
                                    <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{s.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* GUARANTEES */}
            <section className="bg-white border-y border-black/10 py-16" data-testid="ouverture-guarantees">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {[
                        { icon: Clock, t: "24h/24 — 7j/7", d: "Y compris les nuits, week-ends et jours fériés" },
                        { icon: ShieldCheck, t: "Sans casser la serrure", d: "Techniques fines privilégiées dans 9 cas sur 10" },
                        { icon: KeyRound, t: "Tarif transparent", d: "Annoncé avant l'intervention, pas de surprise" },
                    ].map((g) => (
                        <div key={g.t} className="flex items-start gap-4">
                            <div className="h-11 w-11 grid place-items-center bg-[#0A0A0A] text-white flex-shrink-0">
                                <g.icon className="h-5 w-5" strokeWidth={2.2} />
                            </div>
                            <div>
                                <h4 className="font-display font-bold uppercase tracking-tight">{g.t}</h4>
                                <p className="text-sm text-zinc-600 mt-1">{g.d}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <CTABand title="Coincé devant votre porte ?" subtitle="Appelez maintenant — un artisan est en route en moins de 30 minutes." />
        </>
    );
};

export default Ouverture;
